import { Geolocation } from '@fanduel/geolocation-sdk';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import config from '@/config';
import { gameContextSelector } from '@/features/game-context/store/game-context';
import { Platform } from '@/features/game-context/types';
import { startGeolocation } from '@/features/geolocation/store/geolocation';
import {
  GeolocationDomain,
  GeolocationParams,
} from '@/features/geolocation/types';
import { useGeolocationCallback } from '@/features/geolocation/use-geolocation-callback';
import {
  DdEventCategory,
  DdEventType,
  Logger,
} from '@/features/logging/logger';
import { isLobbyEmbeddedStack } from '@/features/wrapper-proxy/resolve-launcher-message-origin';
import { WrapperProxy } from '@/features/wrapper-proxy/wrapper-proxy';
import { useAppDispatch } from '@/store/hooks';

enum GeolocationCountry {
  USA = 'US',
  CANADA = 'CA',
}

const getEnvironment = () => {
  const { env, geocomplyEnv } = config;
  if (env === 'prod') {
    return { type: 'prod' as const };
  }
  if (geocomplyEnv) {
    return geocomplyEnv;
  }

  return { type: 'cert' as const };
};

export const useGeolocationHelpers = () => {
  const dispatch = useAppDispatch();
  const { platform } = useSelector(gameContextSelector);
  const { geolocationCallback } = useGeolocationCallback();
  const {
    serviceName: appName,
    clientAuthToken,
    installerKey,
  } = config.geolocation;

  const isCountryUSA = process.env?.COUNTRY === 'usa';

  const country = useMemo(
    () =>
      config?.geolocation?.country ??
      (isCountryUSA ? GeolocationCountry.USA : GeolocationCountry.CANADA),
    [isCountryUSA],
  );

  const configureGeolocation = useCallback(
    (
      { region, ...session }: GeolocationParams,
      domain: GeolocationDomain = 'casino',
    ) =>
      Geolocation.setConfig({
        appDomain: {
          product: domain,
          region: region.toUpperCase() as Uppercase<string>,
        },
        appName,
        appVersion: process.env.BUILD_VERSION || 'unknown',
        clientAuthToken,
        installerKey,
        environment: getEnvironment(),
        country,
        session,
      }),
    [country, appName, clientAuthToken, installerKey],
  );

  const locateUser = useCallback(
    (params: GeolocationParams, domain: GeolocationDomain = 'casino') => {
      const mobileWrapperParams = [
        params.userId,
        params.userAuthToken,
        params.sessionId,
        config.geolocation.clientAuthToken,
      ] as const;

      Logger.logRumAction({
        name: 'Geolocation triggered',
        useCase: DdEventCategory.Geolocation,
        eventType: DdEventType.Info,
        service: 'geolocation-web',
      });

      if (platform === Platform.Web || platform === Platform.MobileWeb) {
        // Cross-origin game-client cannot call lobby WebWrapper directly; ask launcher.
        if (isLobbyEmbeddedStack()) {
          WrapperProxy.locateUser(domain);
          return;
        }

        Geolocation.init(
          config.geolocation.serviceName,
          process.env.BUILD_VERSION || 'unknown',
        );
        Geolocation.setCallback(geolocationCallback);

        configureGeolocation(params, domain).then(() => {
          Geolocation.locateUser('game launch');
        });
      } else {
        dispatch(startGeolocation());
        if (
          window?.AndroidWrapper?.triggerLocateUserWithDomain ||
          window?.MobileWrapper?.triggerLocateUserWithDomain
        ) {
          window?.AndroidWrapper?.triggerLocateUserWithDomain?.(
            ...mobileWrapperParams,
            domain,
          );
          window?.MobileWrapper?.triggerLocateUserWithDomain?.(
            ...mobileWrapperParams,
            domain,
          );
        } else {
          window?.AndroidWrapper?.triggerLocateUser?.(...mobileWrapperParams);
          window?.MobileWrapper?.triggerLocateUser?.(...mobileWrapperParams);
        }
      }
    },
    [configureGeolocation, platform, dispatch, geolocationCallback],
  );

  return { locateUser };
};
